import { Button } from "primereact/button";
import { useEffect, useMemo } from "react";
import { purifyPanelData } from "../../purifyExerciseMethods";
import WizardPanelSection from "./wizardPanelSection";
import { formatCarePlanName } from "../../format";

const Step3 = ({
  step,
  setStep,
  manuelSelectedExercises,
  setManuelSelectedExercises,
  selectedPanels,
  setSelectedPanels,
  setCarePlanName,
  carePlanName,
  selectedExercises,
  activePanelIndex,
  setActivePanelIndex,
  setSelectedExercises,
  applyButtonVisibleSecondStep,
  applyButtonVisibleFirstStep,
  panels,
  setIsEditModeActive,
  setIsCollapsedDetails,
  editedExercises,
  setEditedExercises,
  clickedPanels,
  setClickedPanels,
  t,
}: any) => {
  const panelExerciseIds = useMemo(() => {
    const ids = new Set<any>();
    for (const p of selectedPanels ?? []) {
      for (const ep of p?.exerciseProcedure ?? []) {
        ids.add(ep?.procedureId);
      }
    }
    return ids;
  }, [selectedPanels]);

  const manuelSelectedIds = useMemo(() => {
    return new Set<any>(
      (manuelSelectedExercises ?? []).map((w: any) => w?.exercise?.id)
    );
  }, [manuelSelectedExercises]);

  const panelsByExerciseId = useMemo(() => {
    const map = new Map<any, any[]>();

    for (const p of selectedPanels ?? []) {
      const panelId = p?.id;
      if (!panelId) continue;

      for (const ep of p?.exerciseProcedure ?? []) {
        const exId = ep?.procedureId;
        if (!exId) continue;

        if (!map.has(exId)) {
          map.set(exId, [panelId]);
        } else {
          const arr = map.get(exId)!;
          if (!arr.includes(panelId)) {
            arr.push(panelId);
          }
        }
      }
    }

    return map;
  }, [selectedPanels]);

  const findPanelNameByExerciseId = (exerciseId: any) => {
    const panel = panels?.find((p: any) =>
      p.exerciseProcedure?.some(
        (ep: any) => ep.procedureId === exerciseId
      )
    );

    return panel?.name ?? "";
  };


  useEffect(() => {
    if (selectedPanels.length === 0 && manuelSelectedExercises.length === 0) {
      setCarePlanName("");
      return;
    }
      
    if(carePlanName && carePlanName.trim() !== "") return;

    if (selectedPanels.length > 0) {
      const firstPanelName = selectedPanels[0]?.name;
      setCarePlanName(formatCarePlanName(firstPanelName) ?? "");
      return;
    }

    if (manuelSelectedExercises.length > 0) {
      const firstExerciseId =
        manuelSelectedExercises[0]?.exercise?.id;

      const panelName =
        findPanelNameByExerciseId(firstExerciseId);

      setCarePlanName(formatCarePlanName(panelName) ?? "");
    }
  })

  const handleSelectPanel = (panel: any, isSelected: any) => {
    if(selectedPanels.length === 0 && carePlanName?.trim() === "") {
      setCarePlanName(
        formatCarePlanName(panel?.name) ?? ""
      );
    }

    if (isSelected) {
      setClickedPanels((prev:any) => {
        return prev.filter((p:any) => p !== panel.id)
      })

      const removedPanelExerciseIds = new Set<any>(
        (panel?.exerciseProcedure ?? []).map((ep: any) => ep?.procedureId)
      );

      setSelectedPanels((prevPanels: any) => {
        const withoutClickedPanel = prevPanels.filter(
          (p: any) => p.id !== panel.id
        );

        const affectedPanels = withoutClickedPanel.filter((p: any) => {
          return p.exerciseProcedure?.some((ep: any) =>
            removedPanelExerciseIds.has(ep.procedureId)
          );
        });

        const remainingPanels = withoutClickedPanel.filter(
          (p: any) => !affectedPanels.some((ap: any) => ap.id === p.id)
        );

        const ids = remainingPanels.map((p:any) => p.id);
        setClickedPanels((prev:any) => {
          return prev.filter((p:any) => ids.includes(p));
        })

        const exercisesToAddManually: any[] = [];
        for (const affectedPanel of affectedPanels) {
          const otherExercises = affectedPanel.exerciseProcedure?.filter(
            (ep: any) => !removedPanelExerciseIds.has(ep.procedureId)
          ) ?? [];
          
          const purified = purifyPanelData(otherExercises);
          exercisesToAddManually.push(...purified);
        }

        if (exercisesToAddManually.length > 0) {
          setManuelSelectedExercises((prevManuel: any) => {
            const existingIds = new Set(
              prevManuel.map((m: any) => m.exercise.id)
            );
            
            const uniqueToAdd = exercisesToAddManually.filter(
              (ex: any) => !existingIds.has(ex.exercise.id)
            );
            
            return [...prevManuel, ...uniqueToAdd];
          });
        }

        return remainingPanels;
      });

      setManuelSelectedExercises((prevManuel: any) =>
        prevManuel.filter((ex: any) => {
          const exId = ex?.exercise?.id;
          return !removedPanelExerciseIds.has(exId);
        })
      );
    } else {
      setClickedPanels((prev:any) => [...prev, panel.id])

      const addedPanelExerciseIds = new Set<any>(
        (panel?.exerciseProcedure ?? []).map((ep: any) => ep?.procedureId)
      );

      setSelectedPanels((prevPanels: any) => {
        const selectedPanelIds = new Set(prevPanels.map((p: any) => p.id));

        if (selectedPanelIds.has(panel.id)) {
          return prevPanels;
        }

        const manualExerciseIds = new Set(
          manuelSelectedExercises.map((m: any) => m.exercise.id)
        );

        const allSelectedExerciseIds = new Set<any>();
        for (const p of prevPanels) {
          for (const ep of p.exerciseProcedure ?? []) {
            allSelectedExerciseIds.add(ep.procedureId);
          }
        }

        for (const id of addedPanelExerciseIds) {
          allSelectedExerciseIds.add(id);
        }

        for (const id of manualExerciseIds) {
          allSelectedExerciseIds.add(id);
        }

        const completedPanels = (panels ?? []).filter((p: any) => {
          if (p.id === panel.id || selectedPanelIds.has(p.id)) {
            return false;
          }

          const panelExerciseIds = (p.exerciseProcedure ?? []).map(
            (ep: any) => ep.procedureId
          );

          if (panelExerciseIds.length === 0) return false;

          return panelExerciseIds.every((id: any) =>
            allSelectedExerciseIds.has(id)
          );
        });

        if (completedPanels.length > 0) {
          const completedPanelExerciseIds = new Set<any>();
          for (const cp of completedPanels) {
            for (const ep of cp.exerciseProcedure ?? []) {
              completedPanelExerciseIds.add(ep.procedureId);
            }
          }

          setManuelSelectedExercises((prevManuel: any) =>
            prevManuel.filter(
              (m: any) => !completedPanelExerciseIds.has(m.exercise.id)
            )
          );
        }

        return [...prevPanels, ...completedPanels, panel];
      });
    }
  };

  const handleAddToPrescription = () => {
    const panelExercises =
      selectedPanels?.flatMap((p: any) =>
        purifyPanelData(p?.exerciseProcedure)
      ) ?? [];

    const allToAdd = [
      ...panelExercises,
      ...(manuelSelectedExercises ?? []),
    ];

    const editedExercisesMap = new Map(
      editedExercises?.map((e: any) => [e?.exercise?.id, e]) ?? []
    );

    const newExercisesMap = new Map<string, any>();

    for (const exerciseToAdd of allToAdd) {
      const exId = exerciseToAdd?.exercise?.id;
      if (!exId) continue;

      const panelIds = panelsByExerciseId.get(exId) ?? [];
      const editedVersion = editedExercisesMap.get(exId);
      const exerciseData = editedVersion ?? exerciseToAdd;

      if (!newExercisesMap.has(exId)) {
        newExercisesMap.set(exId, {
          ...exerciseData,
          sourcePanels: [...panelIds],
        });
      } else {
        const existing = newExercisesMap.get(exId);

        for (const pid of panelIds) {
          if (!existing.sourcePanels.includes(pid)) {
            existing.sourcePanels.push(pid);
          }
        }
      }
    }

    const newExercises = Array.from(newExercisesMap.values()).filter(
      (exercise: any) => {
        const exerciseId = exercise?.exercise?.id;

        const inSelected = panelExerciseIds.has(exerciseId);

        const isManual =
          manuelSelectedIds.has(exerciseId) ||
          (Array.isArray(exercise?.sourcePanels) &&
            exercise.sourcePanels.length === 0);

        return inSelected || isManual;
      }
    );

    const existingOrder = new Map(
      editedExercises?.map((e: any, index: number) => [e?.exercise?.id, index]) ?? []
    );

    let nextOrder = editedExercises?.length ?? 0;

    const finalExercises = newExercises
      .map((exercise: any) => {
        const exId = exercise?.exercise?.id;
        const existingOrderIndex = existingOrder.get(exId);
        
        return {
          ...exercise,
          orderIndex: existingOrderIndex !== undefined ? existingOrderIndex : nextOrder++
        };
      })
      .sort((a: any, b: any) => a.orderIndex - b.orderIndex)
      .map(({ orderIndex, ...rest }: any) => rest);

    setSelectedExercises(finalExercises);
    setEditedExercises(finalExercises);
  };

  const handleAddExercise = (panelId:any,exerciseProcedure: any, isSelected: boolean) => {
    if(selectedPanels.length === 0 && carePlanName?.trim() === "") {
      const panel = panels.find((p:any) => p.id === panelId);
      setCarePlanName(
        formatCarePlanName(panel?.name) ?? ""
      );
    }

    const exId = exerciseProcedure?.procedureId;
    if (!exId) return;

    const isInClickedPanels = clickedPanels?.find((p:any) => p === panelId);
    if(!isInClickedPanels) {
      setClickedPanels((prev:any) => {
        return [...prev, panelId]
      })
    }

    if (isSelected) {
      const parentPanels = selectedPanels.filter(
        (panel: any) =>
          panel.exerciseProcedure?.some(
            (ep: any) => ep.procedureId === exId
          )
      );

      if (parentPanels.length > 0) {
        setSelectedPanels((prev: any) =>
          prev.filter((p: any) => !parentPanels.some((pp: any) => pp.id === p.id))
        );

        const allRemainingPurified: any[] = [];
        for (const parentPanel of parentPanels) {
          const remainingPurified = purifyPanelData(
            parentPanel.exerciseProcedure.filter(
              (ep: any) => ep.procedureId !== exId
            )
          );
          allRemainingPurified.push(...remainingPurified);
        }

        setManuelSelectedExercises((prev: any) => {
          const withoutClicked = prev.filter((m: any) => m.exercise.id !== exId);
          
          const existingIds = new Set(withoutClicked.map((m: any) => m.exercise.id));
          
          return [
            ...withoutClicked,
            ...allRemainingPurified.filter(
              (p: any) => !existingIds.has(p.exercise.id)
            ),
          ];
        });
        return;
      }

      setManuelSelectedExercises((prev: any) =>
        prev.filter((m: any) => m.exercise.id !== exId)
      );
      return;
    }

    const purified = purifyPanelData([exerciseProcedure])[0];
    if (!purified) return;

    if (manuelSelectedExercises.some((m: any) => m.exercise.id === exId)) return;

    const updatedManuel = [...manuelSelectedExercises, purified];
    
    const selectedPanelExerciseIds = new Set<any>();
    for (const panel of selectedPanels ?? []) {
      for (const ep of panel?.exerciseProcedure ?? []) {
        selectedPanelExerciseIds.add(ep?.procedureId);
      }
    }

    const manualExerciseIds = new Set(updatedManuel.map((m: any) => m.exercise.id));

    const allSelectedExerciseIds = new Set([
      ...selectedPanelExerciseIds,
      ...manualExerciseIds,
    ]);
    
    const completedPanels = (panels ?? []).filter(
      (panel: any) => {
        const panelExerciseIds = panel?.exerciseProcedure?.map(
          (ep: any) => ep.procedureId
        );

        if (!panelExerciseIds?.includes(exId)) {
          return false;
        }

        return panelExerciseIds.every((id: any) =>
          allSelectedExerciseIds.has(id)
        );
      }
    );

    if (completedPanels.length > 0) {
      setSelectedPanels((prevPanels: any) => {
        const selectedPanelIds = new Set(prevPanels.map((p: any) => p.id));
        
        const newPanels = completedPanels.filter(
          (panel: any) => !selectedPanelIds.has(panel.id)
        );

        if (newPanels.length === 0) return prevPanels;

        return [...prevPanels, ...newPanels];
      });
    }

    setManuelSelectedExercises(updatedManuel);
  };

  const isExerciseSelected = (exerciseProcedure: any) => {
    const id = exerciseProcedure?.procedureId;
    return (
      (selectedExercises ?? []).some((s: any) => s?.exercise?.id === id) ||
      manuelSelectedIds.has(id) ||
      panelExerciseIds.has(id)
    );
  };

  const isClickable = () => {
    if (step === 4) return true;
    if (step === 2 && (selectedPanels.length !== 0 || manuelSelectedExercises.length !== 0) && !applyButtonVisibleSecondStep) return true;
    if (step === 1 && (selectedPanels.length !== 0 || manuelSelectedExercises.length !== 0) && !applyButtonVisibleFirstStep) return true;
    return false;
  };

  return (
    <div
      style={{
        flex: step === 3 ? "0 0 41.666667%" : step === 4 ? "0 1 12%" : "1 1 0%",
        backgroundColor: step === 3 ? "#D4DDFF" : "#E8EDFF",
        borderRadius: "16px",
        padding: "1.5rem",
        position: "relative",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "flex 0.4s ease-in-out, background-color 0.4s ease-in-out",
        minWidth: 0,
        cursor: isClickable() ? "pointer" : "default",
      }}
      onClick={
        isClickable()
          ? () => {
              setStep(3);
              setIsEditModeActive(false);
              setIsCollapsedDetails(false);
              setSelectedExercises([]);
            }
          : undefined
      }
    >
      {((step === 1 || step === 2) && selectedPanels.length === 0 && manuelSelectedExercises.length === 0) && (
        <div className="w-full h-full flex flex-column gap-4 justify-content-center align-items-center">
          <img src="/infoIcon2.svg" />
          <div className="flex flex-column justify-content-center align-items-center">
            <p className="m-0 font-bold text-xl " style={{ color: "#2D3967" }}>
              {t("panels")}
            </p>
            <p className="m-0 text-sm" style={{ color: "#727A99" }}>
              {t("panelsDescription")}
            </p>
          </div>
        </div>
      )}

      <div
        className="flex flex-column"
        style={{ flex: 1, minHeight: 0, overflow: "auto" }}
      >
        <div className="flex flex-column ">
          {step === 3 && (
            <p
              className="m-0 font-bold text-xl mb-5"
              style={{ color: "#2D3967" }}
            >
              {t("selectThePanels")}
            </p>
          )}
          {step !== 3 && (
            <p
              className="m-0 font-bold text-xl mb-5"
              style={{ color: "#2D3967" }}
            >
              {t("panels")}
            </p>
          )}
          {(panels?.length > 0) ? (
            <WizardPanelSection
              t={t}
              activeIndex={activePanelIndex}
              setActiveIndex={setActivePanelIndex}
              myPanels={panels}
              selectedPanels={selectedPanels}
              handleSelectPanel={handleSelectPanel}
              step={step}
              isExerciseSelected={isExerciseSelected}
              handleAddExercise={handleAddExercise}
              clickedPanels={clickedPanels}
            />
          ) : (
            <p
              className="m-0 text-lg text-center"
              style={{ color: "#2D3967" }}
            >
              {t("noMatchingPanelsFound")}
            </p>
          )}
        </div>
      </div>

      {step === 3 && (
        <div
          className="flex justify-content-between align-items-center"
          style={{
            backgroundColor: "#D4DDFF",
            paddingTop: "1rem",
            marginTop: "auto",
            flexShrink: 0,
          }}
        >
          <div
            className="flex align-items-center justify-content-center  gap-2"
            onClick={() => {
              setSelectedPanels([]);
              setEditedExercises([]);
              setManuelSelectedExercises([]);
              setClickedPanels([]);
            }}
            style={{ cursor: "pointer" }}
          >
            <i className="pi pi-trash" style={{ color: "#727A99" }}></i>
            <p
              className="m-0 text-sm underline"
              style={{ color: "#727A99" }}
            >
              {t("clearChoice")}
            </p>
          </div>

          <Button
            disabled={
              selectedPanels.length === 0 &&
              manuelSelectedExercises.length === 0
            }
            label={t("addToPrescription")}
            className="p-3"
            style={{
              backgroundColor: "#6383F4",
              color: "#FFFFFF",
              borderRadius: "16px",
              border: "none",
              width: "15rem",
              height: "2.5rem",
            }}
            onClick={() => {
              handleAddToPrescription();
              setActivePanelIndex(-1);
              setStep(4);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Step3;
